import type { ApolloClient } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { BasePlugin, type PluginOpts } from '@uppy/core';

import type { AssetContext } from 'bundles/asset-admin/types/assets';
import type { AssetAssembly, TBody, TMeta, UppyWithMeta } from 'bundles/asset-admin/types/uppy';
import {
  createAssetCreationAttempt,
  failAssetCreationAttempt,
} from 'bundles/asset-admin/utils/AssetCreationAttemptsApiUtils';

import _t from 'i18n!nls/asset-admin';

type AssetAttemptPluginOptions = PluginOpts & {
  enabled: boolean;
  assetContext: AssetContext | { courseId?: string | undefined };
  pdfTemplate?: string | {};
  id?: string;
  assetCreationUrl?: string;
  apolloClient?: ApolloClient<unknown>;
};
export default class AssetAttemptPlugin extends BasePlugin<AssetAttemptPluginOptions, TMeta, TBody> {
  declare opts: AssetAttemptPluginOptions;

  constructor(uppy: UppyWithMeta, opts?: AssetAttemptPluginOptions) {
    super(uppy, opts);
    this.id = opts?.id || 'AssetAttemptPlugin';
    this.type = 'AssetAttemptPlugin';
    this.uppy = uppy;
    if (opts) {
      this.opts = opts;
    }
  }

  // create an asset creation attempt before starting the transloadit upload process
  createAssetCreationAttempt = async (fileIds: Array<string>) => {
    const { pdfTemplate, assetContext, assetCreationUrl, apolloClient } = this.opts;

    const files = this.uppy.getFiles().filter((file) => fileIds.includes(file.id));
    await Promise.all(
      files.map((file) => {
        const options = Object.assign(
          {},
          { name: file.name },
          { pdfTemplate: typeof pdfTemplate === 'string' ? pdfTemplate : undefined },
          file.meta
        );
        const assetContextWithCourseId = { ...assetContext, courseId: assetContext.courseId ?? '' };

        return createAssetCreationAttempt(options, assetContextWithCourseId, file, assetCreationUrl, apolloClient).then(
          (attemptedAsset) => {
            const { id, attemptId } = attemptedAsset;
            this.uppy.setFileMeta(id, { attemptId });
            this.uppy.emit('preprocess-progress', file, {
              mode: 'indeterminate',
              message: _t('Preparing Upload...'),
            });
          }
        );
      })
    );

    files.forEach((file) => {
      this.uppy.emit('preprocess-complete', file);
    });
  };

  failAssetCreationAttempt = async (error: Error & { assembly?: AssetAssembly }) => {
    if (error.assembly) {
      await failAssetCreationAttempt(error.assembly);
      Sentry.captureException('Asset Creation Attempt Failure', { extra: { error } });
    }
  };

  install = () => {
    if (this.opts.enabled) {
      this.uppy.addPreProcessor(this.createAssetCreationAttempt);
      this.uppy.on('error', this.failAssetCreationAttempt);
    }
  };

  uninstall = () => {
    if (this.opts.enabled) {
      this.uppy.removePreProcessor(this.createAssetCreationAttempt);
      this.uppy.off('error', this.failAssetCreationAttempt);
    }
  };
}
